import type { SettingsState } from "src/common/settings"
import type { NavSectionProps } from "src/common/nav-section"
import type { Theme, SxProps, CSSObject, Breakpoint } from "@mui/material/styles"

import { useMemo } from "react"
import { useLocation } from "react-router-dom"
import { useAuthStore, useAppConfigStore } from "@/src/store"
import { isJobDetails } from "@/src/utils/storage-available"
import { featureFlags } from "src/config/featureFlags"
import { paths } from "src/routes/paths"

import { Stack, Divider, Box, Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"

import { useBoolean } from "src/hooks/use-boolean"
import useIsMobile from "@/src/hooks/useIsMobile"

import { varAlpha, stylesMode } from "src/theme/styles"

import { bulletColor } from "src/common/nav-section"
import { useSettingsStore } from "@/src/store"

import { Main } from "./main"
import { NavMobile } from "./nav-mobile"
import { layoutClasses } from "../classes"
import { NavVertical } from "./nav-vertical"
import { LayoutSection } from "../core/layout-section"
import { AccountDrawer } from "../components/account-drawer"
import { SettingsButton } from "../components/settings-button"
import { WorkspaceOptions } from "./workspaceOptions"
import { navData as dashboardNavData } from "../config/config-nav-dashboard"
import { internalUserNavData } from "../config/InternalUserNavData"
import { MobileNavProvider } from "./mobileNavContext"

const INTERNAL_USER_ROOT = "/internal-user"

// ----------------------------------------------------------------------

export type DashboardLayoutProps = {
    sx?: SxProps<Theme>
    children: React.ReactNode
    data?: {
        nav?: NavSectionProps["data"]
    }
}

export function DashboardLayout({ sx, children, data }: DashboardLayoutProps) {
    const theme = useTheme()
    const isMobile = useIsMobile()
    const location = useLocation()

    const mobileNavOpen = useBoolean()

    const settings = useSettingsStore()

    const appConfig = useAppConfigStore((state) => state.appConfig)

    const navColorVars = useNavColorVars(theme, settings.settings)

    const layoutQuery: Breakpoint = "lg"

    // Force mini mode for sidebar - hover will expand temporarily
    const isNavMini = true

    const isNavHorizontal = false

    const isNavVertical = true

    const user = useAuthStore((state) => state.user)

    const isInternalUser = location.pathname.startsWith(INTERNAL_USER_ROOT)

    const currentNavData = useMemo(() => (isInternalUser ? internalUserNavData : dashboardNavData), [isInternalUser])

    /**
     * Using this entrypoint to check the user auth status and the route/permissions boundary across routes
     */
    const navData =
        data?.nav ??
        currentNavData.map((v) => ({
            ...v,
            items: v.items.filter((i: any) => {
                if (isInternalUser) {
                    if (i.path === paths.internalUser.prompts && !featureFlags.superAdminPromptPolicies) {
                        return false
                    }
                    return true
                }
                if (i.permissions) {
                    return i.permissions.every((j: any) => user?.permissions?.includes(j))
                }
                if (appConfig?.disableAddCandidates) {
                    if (i.title === "History" || i.title === "Get Started") {
                        return false
                    }
                }
                return true
            }),
        }))

    return (
        <>
            <NavMobile
                data={navData}
                open={mobileNavOpen.value}
                onClose={mobileNavOpen.onFalse}
                cssVars={navColorVars.section}
                slots={{
                    topArea: isInternalUser ? <Box sx={{ pl: 3.5, pt: 2.5, pb: 1 }} /> : undefined,
                    bottomArea: (
                        <Stack
                            direction="column"
                            spacing={2}
                            sx={{
                                borderTop: "1px solid",
                                borderColor: "divider",
                                pt: 2.5,
                                pb: 2,
                                px: 2,
                            }}
                        >
                            {/* Settings Button - hidden for internal users */}
                            {!isInternalUser && (
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1.5}
                                    onClick={settings.onToggleDrawer}
                                    sx={{
                                        cursor: "pointer",
                                        borderRadius: 1,
                                        "&:hover": {
                                            bgcolor: "action.hover",
                                        },
                                    }}
                                >
                                    <SettingsButton data-slot="settings" />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Settings
                                    </Typography>
                                </Stack>
                            )}

                            {/* User Account */}
                            <Box
                                sx={{
                                    borderRadius: 1,
                                    "&:hover": {
                                        bgcolor: "action.hover",
                                    },
                                }}
                            >
                                <AccountDrawer
                                    data-slot="account"
                                    isExpanded={true}
                                    hideWorkspaceLogo={isInternalUser}
                                />
                            </Box>

                            {/* Workspace Selector - only for dashboard, not internal user */}
                            {!isInternalUser && (
                                <Box>
                                    <WorkspaceOptions />
                                </Box>
                            )}
                        </Stack>
                    ),
                }}
            />

            <MobileNavProvider openMobileNav={mobileNavOpen.onTrue}>
                <LayoutSection
                    /** **************************************
                     * Header
                     *************************************** */
                    headerSection={null}
                    /** **************************************
                     * Sidebar
                     *************************************** */
                    sidebarSection={
                        isNavHorizontal || isJobDetails() ? null : (
                            <NavVertical
                                data={navData}
                                layoutQuery={layoutQuery}
                                cssVars={navColorVars.section}
                                slots={{
                                    bottomArea: ({ isExpanded }) => (
                                        <Stack
                                            direction="column"
                                            spacing={isExpanded ? 2 : 1}
                                            sx={{
                                                borderTop: isExpanded ? "1px solid" : "none",
                                                borderColor: "divider",
                                                pt: isExpanded ? 2.5 : 2,
                                                pb: isExpanded ? 2 : 1,
                                                px: isExpanded ? 2 : 0,
                                            }}
                                        >
                                            {isExpanded ? (
                                                <>
                                                    {/* Settings Button - hidden for internal users */}
                                                    {!isInternalUser && (
                                                        <Stack
                                                            direction="row"
                                                            alignItems="center"
                                                            spacing={1.5}
                                                            onClick={settings.onToggleDrawer}
                                                            sx={{
                                                                cursor: "pointer",
                                                                borderRadius: 1,
                                                                "&:hover": {
                                                                    bgcolor: "action.hover",
                                                                },
                                                            }}
                                                        >
                                                            <SettingsButton data-slot="settings" />
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                Settings
                                                            </Typography>
                                                        </Stack>
                                                    )}

                                                    {/* User Account */}
                                                    <Box
                                                        sx={{
                                                            borderRadius: 1,
                                                            "&:hover": {
                                                                bgcolor: "action.hover",
                                                            },
                                                        }}
                                                    >
                                                        <AccountDrawer
                                                            data-slot="account"
                                                            isExpanded={isExpanded}
                                                            hideWorkspaceLogo={isInternalUser}
                                                        />
                                                    </Box>

                                                    {/* Workspace Selector - only for dashboard, not internal user */}
                                                    {!isInternalUser && (
                                                        <Box>
                                                            <WorkspaceOptions />
                                                        </Box>
                                                    )}
                                                </>
                                            ) : (
                                                <Stack direction="column" spacing={1} alignItems="center">
                                                    {!isInternalUser && <SettingsButton data-slot="settings" />}
                                                    <AccountDrawer
                                                        data-slot="account"
                                                        isExpanded={isExpanded}
                                                        hideWorkspaceLogo={isInternalUser}
                                                    />
                                                </Stack>
                                            )}
                                        </Stack>
                                    ),
                                }}
                            />
                        )
                    }
                    /** **************************************
                     * Footer
                     *************************************** */
                    footerSection={null}
                    /** **************************************
                     * Style
                     *************************************** */
                    cssVars={{
                        ...navColorVars.layout,
                        "--layout-transition-easing": "cubic-bezier(0.4, 0.0, 0.2, 1)",
                        "--layout-transition-duration": "300ms",
                        "--layout-nav-mini-width": "88px",
                        "--layout-nav-vertical-width": "300px",
                        "--layout-nav-horizontal-height": "64px",
                        "--layout-dashboard-content-pt": theme.spacing(1),
                        "--layout-dashboard-content-pb": theme.spacing(6),
                        "--layout-dashboard-content-px": theme.spacing(5),
                    }}
                    sx={{
                        [`& .${layoutClasses.hasSidebar}`]: {
                            // Smooth transitions for all breakpoints
                            transition: theme.transitions.create(["padding-left"], {
                                easing: "var(--layout-transition-easing)",
                                duration: "var(--layout-transition-duration)",
                            }),
                            pl: isMobile
                                ? 0
                                : isNavMini
                                  ? "var(--layout-nav-mini-width)"
                                  : "var(--layout-nav-vertical-width)",
                        },
                        ...sx,
                    }}
                >
                    <Main isNavHorizontal={isNavHorizontal}>{children}</Main>
                </LayoutSection>
            </MobileNavProvider>
        </>
    )
}

// ----------------------------------------------------------------------

function useNavColorVars(theme: Theme, settings: SettingsState): Record<"layout" | "section", CSSObject> {
    const {
        vars: { palette },
    } = theme

    return useMemo(() => {
        switch (settings.navColor) {
            case "integrate":
                return {
                    layout: {
                        "--layout-nav-bg": palette.background.default,
                        "--layout-nav-horizontal-bg": varAlpha(palette.background.defaultChannel, 0.8),
                        "--layout-nav-border-color": varAlpha(palette.grey["500Channel"], 0.12),
                        "--layout-nav-text-primary-color": palette.text.primary,
                        "--layout-nav-text-secondary-color": palette.text.secondary,
                        "--layout-nav-text-disabled-color": palette.text.disabled,
                        [stylesMode.dark]: {
                            "--layout-nav-border-color": varAlpha(palette.grey["500Channel"], 0.08),
                            "--layout-nav-horizontal-bg": varAlpha(palette.background.defaultChannel, 0.96),
                        },
                    },
                    section: {},
                }
            case "apparent":
                return {
                    layout: {
                        "--layout-nav-bg": palette.grey[900],
                        "--layout-nav-horizontal-bg": varAlpha(palette.grey["900Channel"], 0.96),
                        "--layout-nav-border-color": "transparent",
                        "--layout-nav-text-primary-color": palette.common.white,
                        "--layout-nav-text-secondary-color": palette.grey[500],
                        "--layout-nav-text-disabled-color": palette.grey[600],
                        [stylesMode.dark]: {
                            "--layout-nav-bg": palette.grey[800],
                            "--layout-nav-horizontal-bg": varAlpha(palette.grey["800Channel"], 0.8),
                        },
                    },
                    section: {
                        // caption
                        "--nav-item-caption-color": palette.grey[600],
                        // subheader
                        "--nav-subheader-color": palette.grey[600],
                        "--nav-subheader-hover-color": palette.common.white,
                        // item
                        "--nav-item-color": palette.grey[500],
                        "--nav-item-root-active-color": palette.primary.light,
                        "--nav-item-root-open-color": palette.common.white,
                        // bullet
                        "--nav-bullet-light-color": bulletColor.dark,
                        // sub
                        ...(settings.navLayout === "vertical" && {
                            "--nav-item-sub-active-color": palette.common.white,
                            "--nav-item-sub-open-color": palette.common.white,
                        }),
                    },
                }
            default:
                throw new Error(`Invalid color: ${settings.navColor}`)
        }
    }, [
        palette.background.default,
        palette.background.defaultChannel,
        palette.common.white,
        palette.grey,
        palette.primary.light,
        palette.text.disabled,
        palette.text.primary,
        palette.text.secondary,
        settings.navColor,
        settings.navLayout,
    ])
}
